import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import styles from "./InternalCommentLiveMode.module.css";

const InternalCommentLiveModeBase = observer(() => {
    const { agendaitemStore } = useContext(RootStoreContext);
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [shouldShowToggle, setShouldShowToggle] = useState(false);
    // Ref for the hidden measurement element
    const measurementRef = useRef<HTMLDivElement>(null);

    // When internal comments change, measure if the content overflows 3 lines.
    useEffect(() => {
        if (measurementRef.current) {
            const { scrollHeight, clientHeight } = measurementRef.current;
            // If scrollHeight is greater than clientHeight, the text is longer than 3 lines.
            setShouldShowToggle(scrollHeight > clientHeight);
        }
    }, [agendaitemStore.currentAgendaItem?.internalcomments]);

    const onClickTitleRow = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    return (
        <UI.Card className={styles.Card}>
            <div className={styles.CardContent}>
                <div className={styles.TitleRow} onClick={onClickTitleRow}>
                    <div className={styles.Title}>{t("screens.assemblies.agenda.internalcomments")}</div>
                    {agendaitemStore.currentAgendaItem?.internalcomments && shouldShowToggle && (
                        <UI.Icon
                            icon={isExpanded ? UI.SVGIcon.ChevronUp : UI.SVGIcon.ChevronDown}
                            className={styles.IconChevronDown}
                            size={24}
                        />
                    )}
                </div>
                <div className={`${styles.Internalcomments} ${!isExpanded ? styles.collapsed : ""}`}>
                    {agendaitemStore.currentAgendaItem?.internalcomments}
                </div>
                {/* Hidden element used for measuring the text height when collapsed */}
                <div
                    ref={measurementRef}
                    className={`${styles.Internalcomments} ${styles.collapsed} ${styles.HiddenMeasurement}`}
                >
                    {agendaitemStore.currentAgendaItem?.internalcomments}
                </div>
            </div>
        </UI.Card>
    );
});

export const InternalCommentLiveMode = Sentry.withProfiler(InternalCommentLiveModeBase);
